import { Button, ButtonStyles, EmptyStateIcons, FloatingSnackbar, Table, Toggle } from '@silverfort/sf-components';
import React, { useCallback, useEffect } from 'react';
import { BaseComponent, ComponentTableColumn, SoftwareStatus } from '../../types';
import {
    buildDisplayedComponents,
    calcStatus,
    getBaseColumns,
    STATUS_FILTER_ITEMS
} from '../../ComponentsOverviewUtils';
import { removeTrailingTags } from '../../../../utils';
import { Banner } from '../../components/Banner';
import {
    AutoDcUpgradeInfo,
    buildComponentsStepFilters,
    DcsUpgradeDisclaimerBanner,
    getComponentName,
    getDcsColumns,
    getNodesColumns,
    getUpgradingFieldByComponentsType,
    handleUpgrade,
    RecommendGradualUpgradeBanner,
    ShouldUpgradeAryaBanner,
    stopGradualUpgrade
} from './UpdateComponentsStepUtils';
import { NODES_TYPES } from '../../EnvironmentOverviewUtils';
import { isMinorVersionUpgrade } from '../UpdateWizardUtils';

const DEFAULT_FILTERS = { connected: [], version: [], status: [], az: [], domains: [], is_protected: [] };

const UpdateComponentsStep: React.FC<{
    softwareStatus: SoftwareStatus;
    components: BaseComponent[];
    componentsType: string;
    allServersData: { nodes: BaseComponent[]; dcs: BaseComponent[]; pushServices: BaseComponent[] };
    handleSendLogs: () => void;
}> = ({ softwareStatus, components, componentsType, allServersData, handleSendLogs }) => {
    const [displayedComponents, setDisplayedComponents] = React.useState<ComponentTableColumn[]>([]);
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [sortSettings, setSortSettings] = React.useState<{ sortBy: string; direction: 'asc' | 'desc' }>({
        sortBy: 'name',
        direction: 'asc'
    });
    const [selectedFilters, setSelectedFilters] = React.useState<{ [key: string]: any[] }>(DEFAULT_FILTERS);
    const [selectedComponents, setSelectedComponents] = React.useState<string[]>(
        components
            .map((comp) => comp.inventoryName)
            .filter(
                (compName) =>
                    softwareStatus.queuedComponents.includes(compName) ||
                    softwareStatus.inProgressComponents.includes(compName)
            )
    );
    const [isGradualUpgrade, setIsGradualUpgrade] = React.useState<boolean>(softwareStatus.queuedComponents.length > 0);
    const [logsSent, setLogsSent] = React.useState<boolean>(false);
    const [upgradeActivated, setUpgradeActivated] = React.useState<boolean>(
        softwareStatus[`upgrading${componentsType.charAt(0).toUpperCase() + componentsType.slice(1)}`]
    );

    const isAutoDcUpgradeStep = componentsType === 'dcs' && softwareStatus.autoADAdapterUpgrade;
    const gradualUpgradeInProgress = isGradualUpgrade && softwareStatus.queuedComponents.length > 0;
    const hideNodesTable =
        componentsType === NODES_TYPES.NODES &&
        (softwareStatus.upgradingPushServices ||
            allServersData.pushServices.find((ps) => ps.version !== softwareStatus.currentTowerVersion));

    const handleSelectedItem = (selectedItems) => {
        const itemsNamesToSelect: string[] = [];

        for (const comp of displayedComponents) {
            if (!isItemDisabled(comp) && selectedItems.includes(comp.inventoryName)) {
                setUpgradeActivated(false);
                itemsNamesToSelect.push(comp.inventoryName);
            }
        }

        setSelectedComponents(itemsNamesToSelect);
    };

    const getColumns = useCallback(() => {
        switch (componentsType) {
            case NODES_TYPES.PUSH_SERVICES:
                return getBaseColumns(softwareStatus);
            case NODES_TYPES.NODES:
                return getNodesColumns(softwareStatus);
            case NODES_TYPES.DCS:
                return getDcsColumns(softwareStatus);
            default:
                return getBaseColumns(softwareStatus);
        }
    }, [componentsType, softwareStatus]);

    useEffect(() => {
        const tableComponents = buildDisplayedComponents({
            searchTerm,
            selectedFilters,
            sortSettings,
            components,
            softwareStatus,
            componentsType
        });

        setDisplayedComponents(
            tableComponents.map((comp) => ({
                name: comp.name,
                version: removeTrailingTags(comp.version),
                connected: comp.connected,
                status: calcStatus(comp.connected, comp.version, comp.inventoryName, softwareStatus, componentsType),
                az: comp.az || [],
                domains: comp.domains || [],
                inventoryName: comp.inventoryName,
                type: comp.type,
                protected: comp.is_protected || false
            }))
        );

        if (softwareStatus[`${componentsType}UpgradeError`]) {
            setUpgradeActivated(false);
        }
    }, [components, searchTerm, softwareStatus, sortSettings, selectedFilters, componentsType]);

    useEffect(() => {
        setSearchTerm('');
        setSortSettings({ sortBy: 'name', direction: 'asc' });
        setSelectedFilters(DEFAULT_FILTERS);
        setSelectedComponents([]);
        setUpgradeActivated(false);
        setIsGradualUpgrade(false);
    }, [componentsType]);

    const handleUpgradeClick = async () => {
        await handleUpgrade({
            setUpgradeActivated,
            setIsGradualUpgrade,
            componentsType,
            selectedComponents,
            isGradualUpgrade
        });
    };

    const handleStopGradualUpgradeClick = async () => {
        setUpgradeActivated(false);
        const cancelledComponents = await stopGradualUpgrade();
        setSelectedComponents((prev) => prev.filter((comp) => !cancelledComponents?.includes(comp)));
    };

    const isItemDisabled = (item) => {
        return (
            (item.status !== STATUS_FILTER_ITEMS.readyForUpgrade.value &&
                item.status !== STATUS_FILTER_ITEMS.failed.value) ||
            softwareStatus[getUpgradingFieldByComponentsType(componentsType)]
        );
    };

    return (
        <>
            {softwareStatus[`${componentsType}UpgradeError`] && !isAutoDcUpgradeStep && (
                <div className="pb-4">
                    <Banner
                        theme="Critical"
                        actionButtonProps={{
                            text: 'Send Logs',
                            onClick: () => {
                                setLogsSent(true);
                                handleSendLogs();
                            },
                            disabled: logsSent
                        }}>
                        <Banner.Body
                            content={`Something went wrong during the upgrade. Download logs to investigate, or send logs and contact support for assistance.`}
                        />
                    </Banner>
                </div>
            )}
            {componentsType === NODES_TYPES.DCS &&
                !isAutoDcUpgradeStep &&
                !isMinorVersionUpgrade(softwareStatus, allServersData.dcs) &&
                DcsUpgradeDisclaimerBanner}
            {hideNodesTable && ShouldUpgradeAryaBanner}
            {componentsType === NODES_TYPES.NODES && !hideNodesTable && RecommendGradualUpgradeBanner}
            {isAutoDcUpgradeStep && AutoDcUpgradeInfo}
            {!isAutoDcUpgradeStep && !hideNodesTable && (
                <div className="flex flex-col gap-4">
                    {components.length > 0 &&
                        buildComponentsStepFilters({
                            components,
                            setSelectedFilters,
                            selectedFilters,
                            setSearchTerm,
                            componentsType,
                            searchTerm
                        })}
                    <Table
                        hideScrollBar={false}
                        onSortChange={(col, direction) => setSortSettings({ sortBy: col as string, direction })}
                        selectable={true}
                        getRowCheckboxProps={(item) => ({
                            disabled: isItemDisabled(item)
                        })}
                        onSelect={handleSelectedItem}
                        disableSelectAll={softwareStatus[getUpgradingFieldByComponentsType(componentsType)]}
                        keyExtractor={(item: { inventoryName: string }) => item.inventoryName}
                        selected={selectedComponents}
                        loading={false}
                        columns={getColumns()}
                        rows={displayedComponents}
                        EmptyStateProps={{
                            icon: EmptyStateIcons.NoItems,
                            title:
                                searchTerm || Object.values(selectedFilters).find((filter) => filter.length)
                                    ? 'No results found. Try adjusting your filters.'
                                    : 'Components haven’t been configured'
                        }}
                    />
                </div>
            )}
            <FloatingSnackbar
                onClose={() => {
                    !isGradualUpgrade && setSelectedComponents([]);
                }}
                open={selectedComponents.length > 0 && (!upgradeActivated || gradualUpgradeInProgress)}
                selections={selectedComponents.length}
                selectionText={{
                    singular: getComponentName(componentsType),
                    plural: `${getComponentName(componentsType)}s`
                }}>
                <div className="flex gap-2 items-center ml-1">
                    {componentsType === NODES_TYPES.NODES && (
                        <div className="flex gap-1 items-center">
                            <Toggle
                                className="toggle"
                                checked={isGradualUpgrade}
                                disabled={upgradeActivated}
                                onChange={() => {
                                    if (isGradualUpgrade) {
                                        setIsGradualUpgrade(false);
                                    } else {
                                        setIsGradualUpgrade(true);
                                        !selectedComponents.length &&
                                            setSelectedComponents(
                                                displayedComponents
                                                    .filter((comp) => comp.status === 'Ready for upgrade')
                                                    .map((comp) => comp.inventoryName)
                                            );
                                    }
                                }}
                            />
                            <span className="text-13 text-sf-white font-sf">Sequential upgrade</span>
                        </div>
                    )}

                    <Button
                        buttonStyle={ButtonStyles.Ghost}
                        onClick={
                            isGradualUpgrade && upgradeActivated ? handleStopGradualUpgradeClick : handleUpgradeClick
                        }
                        text={isGradualUpgrade && upgradeActivated ? 'Stop Upgrade' : 'Upgrade Selected'}
                    />
                </div>
            </FloatingSnackbar>
        </>
    );
};

export default UpdateComponentsStep;
